import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, X, ImageIcon, CheckCircle, ArrowRight, Phone, Sparkles } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { seoData } from '../utils/seoData';
import getIcon from '../utils/iconMapper';
import '../styles/services-prop4.css';
import '../styles/services-modern.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Mariages = () => {
  const navigate = useNavigate();
  const [intro, setIntro] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [hero, setHero] = useState(null);
  const [stats, setStats] = useState([]);
  const [process, setProcess] = useState([]);
  const [cta, setCta] = useState(null);
  const [modalData, setModalData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [introRes, blocksRes, heroRes, statsRes, processRes, ctaRes] = await Promise.all([
          axios.get(`${API}/service-intro/mariages`),
          axios.get(`${API}/service-blocks/mariages`),
          axios.get(`${API}/service-hero/mariages`),
          axios.get(`${API}/service-stats/mariages`),
          axios.get(`${API}/service-process/mariages`),
          axios.get(`${API}/service-cta/mariages`)
        ]);
        setIntro(introRes.data);
        setBlocks(blocksRes.data);
        setHero(heroRes.data);
        setStats(statsRes.data);
        setProcess(processRes.data);
        setCta(ctaRes.data);
      } catch (error) {
        console.error('Error loading service data:', error);
      }
    };
    fetchData();
  }, []);

  const openModal = (imageUrl, title, description) => {
    setModalData({ imageUrl, title, description });
  };

  const closeModal = () => {
    setModalData(null);
  };

  const scrollToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  return (
    <>
      <SEO {...seoData.mariages} />
      <div className="service-detail-page">
        <Breadcrumb items={[
          { label: 'Services', link: '/#services' },
          { label: 'Mariages' }
        ]} />
        {/* Background Video */}
        <div className="page-video-background">
          <iframe
            className="page-background-video"
            src="https://www.youtube.com/embed/AcQHHKsBFyE?autoplay=1&mute=1&loop=1&playlist=AcQHHKsBFyE&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&iv_load_policy=3"
            title="CYGA EVENEMENT - Spectacle Pyrotechnique de fond"
            frameBorder="0"
            allow="autoplay; encrypted-media"
          ></iframe>
        </div>

        {/* Hero Section Moderne - DYNAMIQUE */}
        {hero && (
          <section 
            className="service-detail-hero-modern" 
            style={{
              backgroundImage: `url(${hero.background_image})`
            }}
          >
            <button onClick={() => navigate('/')} className="back-button" style={{ position: 'absolute', top: '2rem', left: '2rem', zIndex: 10 }}>
              <ArrowLeft /> Retour
            </button>
            
            <div className="hero-content-modern">
              <h1 className="hero-title-modern">
                {hero.title_line1} <span className="highlight-modern">{hero.title_line2}</span>
              </h1>
              <p className="hero-subtitle-modern">
                {hero.subtitle}
              </p>
              <div className="hero-cta-group-modern">
                <button className="hero-button-primary-modern" onClick={scrollToContact}>
                  Demander un Devis
                  <ArrowRight size={20} />
                </button>
                <button className="hero-button-secondary-modern" onClick={() => document.getElementById('intro')?.scrollIntoView({ behavior: 'smooth' })}>
                  En savoir plus
                  <Sparkles size={20} />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Section Intro Moderne - DYNAMIQUE */}
        {intro && (
          <section id="intro" className="intro-section-modern">
            <div className="intro-content-modern">
              <div className="intro-text-modern">
                <div className="intro-icon-large-modern">
                  {getIcon(intro.icon_name, { size: 40, color: 'white' })}
                </div>
                <h2 className="intro-title-modern">{intro.title}</h2>
                <p className="intro-description-modern">{intro.description}</p>
                
                <div className="intro-features-modern">
                  <div className="intro-feature-item-modern">
                    <CheckCircle className="intro-feature-icon-modern" size={24} />
                    <span>Spectacles personnalisés selon vos souhaits</span>
                  </div>
                  <div className="intro-feature-item-modern">
                    <CheckCircle className="intro-feature-icon-modern" size={24} />
                    <span>Expertise et savoir-faire reconnus</span>
                  </div>
                  <div className="intro-feature-item-modern">
                    <CheckCircle className="intro-feature-icon-modern" size={24} />
                    <span>Sécurité et professionnalisme garantis</span>
                  </div>
                </div>
              </div>
              
              <div className="intro-visual-modern">
                <img 
                  src={intro.image_url || "https://customer-assets.emergentagent.com/job_cyga-refresh/artifacts/ilg8oney_0532_IMGP9943-Edit.jpg"} 
                  alt="Mariage féérique avec spectacle pyrotechnique CYGA - Feux d'artifice personnalisés pour votre jour J"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </section>
        )}

        {/* Section Stats - DYNAMIQUE */}
        <section className="stats-section-modern">
          <div className="stats-grid-modern">
            {stats.map((stat) => (
              <div key={stat.id} className="stat-item-modern">
                <div className="stat-number-modern">{stat.number}</div>
                <div className="stat-label-modern">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Section Cards Moderne - DYNAMIQUE */}
        <section className="cards-section-modern">
          <div className="section-header-modern">
            <h2 className="section-title-modern">Nos Prestations</h2>
            <p className="section-subtitle-modern">
              Découvrez nos différentes options pour créer le mariage de vos rêves
            </p>
          </div>

          <div className="content-grid-prop4 content-grid-modern">
            {blocks.map((block, index) => (
              <div 
                key={block.id} 
                className="content-card-prop4" 
                onClick={() => block.image_url && openModal(block.image_url, block.title, block.description)}
                data-testid={`feature-card-${index + 1}`}
              >
                {block.image_url ? (
                  <div className="card-image-preview-prop4">
                    <img 
                      src={block.image_url} 
                      alt={`${block.title} - Prestation mariage CYGA EVENEMENT`}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="card-image-overlay-prop4"></div>
                    <div className="image-badge-prop4">
                      <Eye size={18} />
                      Voir l'image
                    </div>
                  </div>
                ) : (
                  <div className="card-no-image-prop4">
                    <ImageIcon size={60} className="card-no-image-icon-prop4" />
                  </div>
                )}
                
                <div className="card-content-prop4">
                  <div className="card-icon-prop4">
                    {getIcon(block.icon_name, { size: 24 })}
                  </div>
                  <h3 className="card-title-prop4">{block.title}</h3>
                  <p className="card-description-prop4">{block.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section Processus - DYNAMIQUE */}
        <section className="process-section-modern">
          <div className="section-header-modern">
            <h2 className="section-title-modern">Notre Processus</h2>
            <p className="section-subtitle-modern">
              Un accompagnement personnalisé de A à Z pour votre mariage
            </p>
          </div>

          <div className="process-timeline-modern">
            {process.map((step, index) => (
              <div key={step.id} className="process-step-modern">
                {index % 2 === 0 ? (
                  <>
                    <div className="process-step-content-modern">
                      <h3 className="process-step-title-modern">{step.title}</h3>
                      <p className="process-step-description-modern">{step.description}</p>
                    </div>
                    <div className="process-step-number-modern">{step.order + 1}</div>
                    <div></div>
                  </>
                ) : (
                  <>
                    <div></div>
                    <div className="process-step-number-modern">{step.order + 1}</div>
                    <div className="process-step-content-modern">
                      <h3 className="process-step-title-modern">{step.title}</h3>
                      <p className="process-step-description-modern">{step.description}</p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA Finale Moderne - DYNAMIQUE */}
        {cta && (
          <section className="final-cta-section-modern">
            <div className="final-cta-content-modern">
              <h2 className="final-cta-title-modern">{cta.title}</h2>
              <p className="final-cta-description-modern">
                {cta.description}
              </p>
              <button className="final-cta-button-modern" onClick={scrollToContact}>
                <Phone size={24} />
                {cta.button_text}
              </button>
            </div>
          </section>
        )}

        {/* Modal Image */}
        <div 
          className={`image-modal-prop4 ${modalData ? 'active' : ''}`}
          onClick={closeModal}
        >
          <div className="modal-content-prop4" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-prop4" onClick={closeModal}>
              <X size={20} />
            </button>
            {modalData && (
              <>
                <img src={modalData.imageUrl} alt={`${modalData.title} - Spectacle pyrotechnique CYGA EVENEMENT`} loading="lazy" />
                <div className="modal-text-content-prop4">
                  <h3 className="modal-title-prop4">{modalData.title}</h3>
                  <p className="modal-description-prop4">{modalData.description}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Mariages;
